import { toast } from 'react-toastify';
import { getToken, getUser } from '../lib/storage';
export interface HttpClientOptions {
  baseUrl?: string;
  timeoutMs?: number;
  retryAttempts?: number;
}

export interface HttpRequestOptions extends RequestInit {
  signal?: AbortSignal;
  authenticate?: boolean; // attach Authorization header
  timeoutMs?: number
}

export interface HttpError extends Error {
  status?: number;
  details?: any;
  // error?:unknown
}

// Error responses come in two shapes across services: the legacy flat
// { error: 'string', message: '...' } and the structured { error: { code, message, field,
// details } } envelope (used by e.g. the Feature Registry). Handle both without breaking
// existing callers that only know the flat shape.
function extractErrorMessage(details: any, fallback: string): string {
  if (!details) return fallback;
  if (details.error && typeof details.error === 'object') return details.error.message || fallback;
  if (typeof details.error === 'string') return details.error;
  if (typeof details.message === 'string') return details.message;
  return fallback;
}

// Message for the thrown Error. Callers have always read `err.message` as the
// response's `message` field, so that keeps priority over the flat `error` string —
// Fastify's default error body is { error: 'Bad Request', message: '<the real reason>' }.
function extractThrownMessage(details: any, fallback: string): string {
  if (details && typeof details.error !== 'object' && typeof details.message === 'string') {
    return details.message;
  }
  return extractErrorMessage(details, fallback);
}

export interface HttpStreamResponse {
  response: Response;
}

// Read envs from CRA
const ENV_BASE_URL =
  (process.env.REACT_APP_API_URL ??
    process.env.REACT_APP_API_BASE_URL) || '';



const ENV_TIMEOUT = Number(process.env.REACT_APP_API_TIMEOUT ?? 30000);

const ENV_RETRIES = Number(process.env.REACT_APP_API_RETRY_ATTEMPTS ?? 3);
const user = getUser();


// helpers to read tenant from env or storage
const ENV_TENANT_ID = user?.tenantId

const ENV_TENANT_SLUG = process.env.REACT_APP_TENANT_SLUG;

function getTenantHeaders() {
  // prefer localStorage so user can switch tenants dynamically
  const lsTenantId = localStorage.getItem('agentic_tenant_id') || localStorage.getItem('tenant_id');
  const lsTenantSlug = localStorage.getItem('agentic_tenant_slug') || localStorage.getItem('tenant_slug');

  const tenantId = lsTenantId || ENV_TENANT_ID;
  const tenantSlug = lsTenantSlug || ENV_TENANT_SLUG;

  const headers: Record<string, string> = {};
  if (tenantId) headers['X-Tenant-ID'] = tenantId;
  else if (tenantSlug) headers['X-Tenant-Slug'] = tenantSlug;

  return headers;
}


// Helper: ensure base has no trailing slash
const trimSlashEnd = (s: string) => s.replace(/\/+$/, '');
// Helper: ensure path starts with one slash
const ensureLeadingSlash = (s: string) => (s.startsWith('/') ? s : `/${s}`);

const DEFAULT_TIMEOUT_MS = ENV_TIMEOUT;
const DEFAULT_RETRIES = ENV_RETRIES;
const DEFAULT_BASE_URL = trimSlashEnd(ENV_BASE_URL);
let refreshPromise: Promise<boolean> | null = null;

const LOGIN_PATH = '/login';

function redirectToLogin() {
  try {
    localStorage.removeItem('agentic_auth_token');
    localStorage.removeItem('agentic_refresh_token');
    localStorage.removeItem('agentic_user_data');

  } catch (e) {
    console.error('Error clearing auth storage', e);
  }
  window.location.href = LOGIN_PATH;
}


export class HttpClient {
  private readonly baseUrl: string;
  private readonly timeoutMs: number;
  private readonly retryAttempts: number;

  constructor(options?: HttpClientOptions) {
    this.baseUrl = trimSlashEnd(options?.baseUrl ?? DEFAULT_BASE_URL);
    this.timeoutMs = options?.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    this.retryAttempts = options?.retryAttempts ?? DEFAULT_RETRIES;
  }
  private getAuthToken(): string | null {
    return getToken();
  }


  private buildUrl(path: string): string {
    if (/^https?:\/\//i.test(path)) return path;
    if (!this.baseUrl) return ensureLeadingSlash(path);
    return `${this.baseUrl}${ensureLeadingSlash(path)}`;
  }



  private async request<T>(path: string, options: HttpRequestOptions = {}): Promise<T> {
    const url = this.buildUrl(path);

    const headers = new Headers(options.headers || {});
    if (options.authenticate) {
      const token = this.getAuthToken()

      if (token) headers.set('Authorization', `Bearer ${token}`);
    }


    const tenantHeaders = getTenantHeaders();
    Object.entries(tenantHeaders).forEach(([k, v]) => {
      if (!headers.has(k)) headers.set(k, v);
    });

    // Debug logging for tenant ID in headers
    if (path.includes('/core/chat/send-message')) {
      console.log('🔍 API Request Debug - Headers:', {
        url,
        'X-Tenant-ID': headers.get('X-Tenant-ID'),
        'X-Tenant-Slug': headers.get('X-Tenant-Slug'),
        tenantHeaders,
      });
    }

    if (options.authenticate) {
      const token = this.getAuthToken();
      if (token) headers.set('Authorization', `Bearer ${token}`);
    }

    const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;

    if (isFormData) {
      headers.delete('Content-Type');
      headers.delete('content-type');
      headers.delete('Content-Length');
      headers.delete('content-length');
    }
    const hasBody = options.body != null;
    if (hasBody && !(options.body instanceof FormData)) {
      if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
    }


    let attempts = 0;
    let lastError: unknown;

    for (let attempt = 0; attempt <= attempts; attempt++) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeoutMs);

      try {
        const resp = await fetch(url, {
          ...options,
          headers,
          signal: options.signal ?? controller.signal, // <-- make timeout effective
        });

        clearTimeout(timeoutId);

        if (!resp.ok) {
          if (!resp.ok) {
            if (resp.status === 401 && options.authenticate) {
              const refreshed = await this.tryRefreshToken();
              if (refreshed) {
                const token = this.getAuthToken();
                if (token) headers.set("Authorization", `Bearer ${token}`);
                attempts++;
                continue;
              }
            }
          }

          const error: HttpError = new Error(`HTTP ${resp.status}`);
          error.status = resp.status;
          try {
            error.details = await resp.json();
            if(resp.status !== 401){
              toast.error(extractErrorMessage(error.details, 'Failed to save agent.'));
            }
          } catch {

          }
          error.message = extractThrownMessage(error.details, error.message);
          throw error;
        }

        const contentType = resp.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          return (await resp.json()) as T;
        }
        return (await resp.text()) as T;
      } catch (err) {
        clearTimeout(timeoutId);
        lastError = err;

        const isAbort = (err as any)?.name === 'AbortError';
        const isLast = attempt === attempts;
        if (isAbort || isLast) break;

        await new Promise((r) => setTimeout(r, 250 * (attempt + 1)));
      }
    }

    throw lastError instanceof Error ? lastError : new Error('Request failed');
  }
  
  private async tryRefreshToken(): Promise<boolean> {
    if (refreshPromise) {
      return refreshPromise;
    }
  
    refreshPromise = (async () => {
      try {
        const refreshToken = localStorage.getItem("agentic_refresh_token");
        if (!refreshToken) {
          redirectToLogin();
          return false;
        }
  
        const url = this.baseUrl
          ? `${this.baseUrl}/auth/refresh-token`
          : "/auth/refresh-token";
  
        const res = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...getTenantHeaders(),
          },
          body: JSON.stringify({ refreshToken }),
        });
  
        if (!res.ok) {
          redirectToLogin();
          return false;
        }
  
        const user = await res.json();
        localStorage.setItem("agentic_auth_token", user.token);
        if (user.refreshToken) {
          localStorage.setItem("agentic_refresh_token", user.refreshToken);
        }
  
        return true;
      } catch (e) {
        console.error("Refresh failed:", e);
        redirectToLogin();
        return false;
      } finally {
        refreshPromise = null;
      }
    })();
  
    return refreshPromise;
  }
  


  public get<T>(path: string, options?: HttpRequestOptions): Promise<T> {
    return this.request<T>(path, { ...options, method: 'GET' });
  }
  public post<T>(path: string, body?: unknown, options?: HttpRequestOptions): Promise<T> {
    // Debug logging for send-message endpoint
    if (path.includes('/core/chat/send-message') && body) {
      console.log('🔍 API Request Debug - Body:', {
        path,
        body,
        tenantId: (body as any)?.tenantId,
      });
    }

    let payload: any = body;
    if (body && !(body instanceof FormData) && typeof body !== 'string') {
      payload = JSON.stringify(body);
    }
    return this.request<T>(path, { ...options, method: 'POST', body: payload });
  }

  public put<T>(path: string, body?: unknown, options?: HttpRequestOptions): Promise<T> {
    const payload =
      body == null || body instanceof FormData || typeof body === 'string'
        ? (body as any)
        : JSON.stringify(body);
    return this.request<T>(path, { ...options, method: 'PUT', body: payload });
  }
  public patch<T>(path: string, body?: unknown, options?: HttpRequestOptions): Promise<T> {
    const payload =
      body == null || body instanceof FormData || typeof body === 'string'
        ? (body as any)
        : JSON.stringify(body);
    return this.request<T>(path, { ...options, method: 'PATCH', body: payload });
  }
  public delete<T>(path: string, options?: HttpRequestOptions): Promise<T> {
    return this.request<T>(path, { ...options, method: 'DELETE' });
  }

  // low-level streaming request (no JSON parsing)
  private async requestStream(path: string, options: HttpRequestOptions = {}): Promise<HttpStreamResponse> {
    const url = this.buildUrl(path);

    const headers = new Headers(options.headers || {});
    if (options.authenticate) {
      const token = this.getAuthToken();
      if (token) headers.set('Authorization', `Bearer ${token}`);
    }

    const tenantHeaders = getTenantHeaders();
    Object.entries(tenantHeaders).forEach(([k, v]) => {
      if (!headers.has(k)) headers.set(k, v);
    });

    // Debug for chat endpoint
    if (path.includes('/core/chat/send-message')) {
      console.log('🔍 API Stream Request Debug - Headers:', {
        url,
        'X-Tenant-ID': headers.get('X-Tenant-ID'),
        'X-Tenant-Slug': headers.get('X-Tenant-Slug'),
        tenantHeaders,
      });
    }

    const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
    if (isFormData) {
      headers.delete('Content-Type');
      headers.delete('content-type');
      headers.delete('Content-Length');
      headers.delete('content-length');
    }
    const hasBody = options.body != null;
    if (hasBody && !(options.body instanceof FormData)) {
      if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
    }

    const controller = new AbortController();
    const timeoutMs = options.timeoutMs ?? this.timeoutMs;
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const resp = await fetch(url, {
        ...options,
        headers,
        signal: options.signal ?? controller.signal,
      });

      clearTimeout(timeoutId);

      if (!resp.ok) {
        // Reuse same error handling pattern as request()
        const error: HttpError = new Error(`HTTP ${resp.status}`);
        error.status = resp.status;
        try {
          error.details = await resp.json();
          if (resp.status !== 401) {
            toast.error(extractErrorMessage(error.details, 'Failed to send message.'));
          }
        } catch {
          // ignore
        }
        error.message = extractThrownMessage(error.details, error.message);
        throw error;
      }

      return { response: resp };
    } catch (err) {
      clearTimeout(timeoutId);
      throw err;
    }
  }

  // streaming POST
  public postStream(path: string, body?: unknown, options?: HttpRequestOptions): Promise<HttpStreamResponse> {
    if (path.includes('/core/chat/send-message') && body) {
      console.log('🔍 API Stream Request Debug - Body:', {
        path,
        body,
        tenantId: (body as any)?.tenantId,
      });
    }

    let payload: any = body;
    if (body && !(body instanceof FormData) && typeof body !== 'string') {
      payload = JSON.stringify(body);
    }

    return this.requestStream(path, { ...options, method: 'POST', body: payload });
  }
}

export const apiClient = new HttpClient();



